import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Snowflake, Waves, UtensilsCrossed, Sparkles, CookingPot,
  ChefHat, ArrowRight, Quote, CheckCircle2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Flame, Snowflake, Waves, UtensilsCrossed, Sparkles, CookingPot,
  ChefHat, CheckCircle2,
};

function EquipmentIcon({ name, className }) {
  const Icon = ICON_MAP[name] || ChefHat;
  return <Icon className={className} strokeWidth={1.5} />;
}

export default function KitchenPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/kitchen-page`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="w-8 h-8 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const price = data.cuisine_per_day || 300;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navigation />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section data-testid="kitchen-hero" className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={data.hero_image}
            alt="Cuisine professionnelle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1C1E]/60 via-[#1C1C1E]/40 to-[#1C1C1E]/70" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-2 mb-6">
            <ChefHat className="w-8 h-8 text-[#C4A962]" strokeWidth={1.5} />
            <span className="text-[#C4A962] text-sm font-medium tracking-[0.25em] uppercase">Cuisine professionnelle</span>
          </div>
          <h1 data-testid="kitchen-hero-title" className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight">
            {data.hero_title}
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-xl mx-auto">{data.hero_subtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              data-testid="kitchen-hero-cta"
              onClick={() => navigate('/#reservation')}
              className="rounded-full px-8 py-3 bg-[#C4A962] text-white hover:bg-[#b5945a] text-base transition-transform active:scale-95"
            >
              Reserver maintenant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById('equipements')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-8 py-3 border-white/40 text-white hover:bg-white/10 text-base"
            >
              Decouvrir les equipements
            </Button>
          </div>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────────── */}
      <section data-testid="kitchen-intro" className="py-20 md:py-28">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <div className="text-center">
            <p className="text-[#595959] text-base md:text-lg leading-relaxed whitespace-pre-line">
              {data.intro_text}
            </p>
          </div>
          <div data-testid="kitchen-gite-salle-notice" className="mt-10 bg-[#2C3E30] text-[#F2F0E9] rounded-2xl p-6 md:p-8 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#C4A962]/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-[#C4A962]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-semibold text-base mb-1">Option disponible avec le Gite + Salle</p>
              <p className="text-[#F2F0E9]/70 text-sm">
                La cuisine professionnelle est reservable uniquement avec la formule Gite + Salle des fetes.
                Elle est ajoutee en option lors de votre reservation : {price} EUR + 5 EUR par invite (part variable minimum 300 EUR).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── EQUIPEMENTS ──────────────────────────────────── */}
      <section id="equipements" data-testid="kitchen-equipment" className="py-20 md:py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-[#C4A962] text-sm font-medium tracking-[0.2em] uppercase">Equipements</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mt-3 tracking-tight">
              Tout pour cuisiner <span className="italic font-normal">comme un chef</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(data.equipment_list || []).map((eq, i) => (
              <div
                key={i}
                data-testid={`equipment-item-${i}`}
                className="group bg-[#FAFAF9] rounded-2xl p-6 border border-[#E5E5E0] hover:border-[#C4A962]/30 hover:shadow-sm transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#C4A962]/10 flex items-center justify-center mb-4 group-hover:bg-[#C4A962]/20 transition-colors">
                  <EquipmentIcon name={eq.icon} className="w-6 h-6 text-[#C4A962]" />
                </div>
                <h3 className="font-semibold text-[#2C3E30] text-base mb-1">{eq.label}</h3>
                {eq.description && <p className="text-sm text-[#595959]">{eq.description}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GALERIE ──────────────────────────────────────── */}
      {data.gallery_images && data.gallery_images.length > 0 && (
        <section data-testid="kitchen-gallery" className="py-20 md:py-28">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <span className="text-[#8A9A5B] text-sm font-medium tracking-[0.2em] uppercase">Galerie</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mt-3 tracking-tight">
                Un espace <span className="italic font-normal">d'exception</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {data.gallery_images.map((img, i) => (
                <div
                  key={i}
                  data-testid={`kitchen-gallery-img-${i}`}
                  className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                    i === 0 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                  onClick={() => setLightbox(img)}
                >
                  <img
                    src={img.url}
                    alt={img.caption || 'Cuisine professionnelle'}
                    className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                      i === 0 ? 'h-64 md:h-full' : 'h-48 md:h-56'
                    }`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    {img.caption && (
                      <p className="absolute bottom-3 left-3 text-white text-sm font-medium">{img.caption}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TEMOIGNAGES ──────────────────────────────────── */}
      {data.testimonials && data.testimonials.length > 0 && (
        <section data-testid="kitchen-testimonials" className="py-20 md:py-28 bg-[#2C3E30]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <span className="text-[#C4A962] text-sm font-medium tracking-[0.2em] uppercase">Temoignages</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mt-3 tracking-tight">
                Ils ont cuisine <span className="italic font-normal">chez nous</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.testimonials.map((t, i) => (
                <div
                  key={i}
                  data-testid={`kitchen-testimonial-${i}`}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                >
                  <Quote className="w-8 h-8 text-[#C4A962]/40 mb-4" strokeWidth={1.5} />
                  <p className="text-[#F2F0E9]/90 text-sm leading-relaxed mb-4">{t.text}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-[#C4A962] text-sm font-medium">{t.author}</p>
                    {t.role && <p className="text-[#F2F0E9]/50 text-xs">{t.role}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CONDITIONS & TARIFS ───────────────────────────── */}
      <section data-testid="kitchen-conditions" className="py-20 md:py-28">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-[#C4A962] text-sm font-medium tracking-[0.2em] uppercase">Tarifs</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#2C3E30] mt-3 tracking-tight">
              Conditions <span className="italic font-normal">et reservation</span>
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E5E0] p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C4A962]/10 mb-6">
              <ChefHat className="w-8 h-8 text-[#C4A962]" strokeWidth={1.5} />
            </div>
            <div className="mb-6">
              <span className="text-5xl font-['Playfair_Display'] text-[#2C3E30]">{price} EUR</span>
              <span className="text-[#595959] text-lg ml-2">+ 5 EUR / invite</span>
              <p className="text-[#8A8A85] text-xs mt-2">Part variable minimum : 300 EUR — Exemple pour 100 invites : {price + Math.max(5 * 100, 300)} EUR</p>
            </div>
            <p className="text-[#595959] text-sm leading-relaxed max-w-lg mx-auto mb-8 whitespace-pre-line">
              {data.conditions_text}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                data-testid="kitchen-conditions-cta"
                onClick={() => navigate('/#reservation')}
                className="rounded-full px-8 py-3 bg-[#C4A962] text-white hover:bg-[#b5945a] text-base"
              >
                Reserver avec la cuisine <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                data-testid="kitchen-caterers-link"
                onClick={() => navigate('/traiteurs-partenaires')}
                className="rounded-full px-8 py-3 text-[#2C3E30] border-[#E5E5E0] hover:bg-[#F5F5F0] text-base"
              >
                Nos traiteurs partenaires
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIGHTBOX ──────────────────────────────────────── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white z-[101]"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.caption || ''}
            className="max-w-full max-h-[85vh] rounded-xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          {lightbox.caption && (
            <p className="absolute bottom-6 text-white text-sm font-medium">{lightbox.caption}</p>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
